import { api } from "@/api/client";

export type RankRow = {
  user_id: string;
  username: string;
  emoji: string;
  avatar_url: string | null;
  points: number;
  correct: number;
  exact: number;
};

function toRankRow(r: any): RankRow {
  return {
    user_id: r.userId,
    username: r.username,
    emoji: r.emoji,
    avatar_url: r.avatarUrl ?? null,
    points: r.totalPoints,
    correct: 0,
    exact: 0,
  };
}

// Loads a slice of the global ranking via the slim /top endpoint.
// With a userIds filter, the server returns exactly those rows (used by
// per-group leaderboards); without one, the top N. Either way the payload
// stays small no matter how many users the Tippspiel has — the old path
// downloaded the ENTIRE ranking (O(users) MB) on every Dashboard mount.
export async function loadRanking(userIds?: string[] | null): Promise<RankRow[]> {
  const res = await api.rankings.top(
    userIds && userIds.length ? { ids: userIds } : { limit: 100 },
  );
  return (res.rows ?? []).map(toRankRow);
}

export type TopRanking = {
  rows: RankRow[];
  total: number;
  // The requesting user's own row — present even when they're outside the
  // returned slice (e.g. rank #4711 with limit=100).
  me: (RankRow & { rank: number }) | null;
};

// Top-N ranking plus the caller's own position + total tipper count.
// limit=0 is valid: just me/total, no rows (Profile header, Dashboard stats).
export async function loadTopRanking(limit = 100): Promise<TopRanking> {
  const res = await api.rankings.top({ limit });
  return {
    rows: (res.rows ?? []).map(toRankRow),
    total: res.total ?? 0,
    me: res.me ? { ...toRankRow(res.me), rank: res.me.rank } : null,
  };
}

// Top groups by member count.
export async function loadTopGroups(limit = 5): Promise<
  { id: string; name: string; slug: string; logo_url: string | null; member_count: number }[]
> {
  const ranks = (await api.rankings.groups()) as any[];
  return ranks
    .slice()
    .sort((a, b) => b.memberCount - a.memberCount)
    .slice(0, limit)
    .map((g) => ({
      id: g.groupId,
      name: g.name,
      slug: g.slug,
      logo_url: g.logoUrl ?? null,
      member_count: g.memberCount,
    }));
}

export type GroupRankingRow = {
  group_id: string;
  name: string;
  slug: string;
  logo_url: string | null;
  member_count: number;
  total_points: number;
  rank: number;
};

export async function loadGroupRankings(): Promise<GroupRankingRow[]> {
  try {
    const ranks = (await api.rankings.groups()) as any[];
    return ranks.map((r) => ({
      group_id: r.groupId,
      name: r.name,
      slug: r.slug,
      logo_url: r.logoUrl ?? null,
      member_count: Number(r.memberCount ?? 0),
      total_points: Number(r.totalPoints ?? 0),
      rank: Number(r.rank ?? 0),
    }));
  } catch (err) {
    console.error("loadGroupRankings", err);
    return [];
  }
}
